import type { CardFormat } from '@/features/dashboard/api/schemas'
import { formatAbbrev } from './format'

export type Axis = { min: number; max: number; ticks: number }

// Candidate tick steps (× a power of ten) and how many intervals an axis may have.
const NICE_STEPS = [1, 2, 2.5, 5]
const MIN_TICKS = 2
const MAX_TICKS = 5

/**
 * Y-axis bounds: of every
 * "nice" step giving 2-5 intervals, the one whose range wastes least space
 * beyond [lo, hi], e.g. 0-600M for a 556M max. Apex's own nice scale rounds
 * up far more aggressively.
 */
export const niceAxis = (lo: number, hi: number): Axis => {
  const range = hi - lo
  if (!(range > 0)) return { min: lo, max: lo + 1, ticks: 1 }
  const exponent = Math.floor(Math.log10(range / MAX_TICKS))
  let best: Axis | null = null
  for (let e = exponent; e <= exponent + 1; e++) {
    for (const multiple of NICE_STEPS) {
      const step = multiple * 10 ** e
      const min = Math.floor(lo / step) * step
      const max = Math.ceil(hi / step) * step
      const ticks = Math.round((max - min) / step)
      if (ticks < MIN_TICKS || ticks > MAX_TICKS) continue
      if (!best || max - min < best.max - best.min) best = { min, max, ticks }
    }
  }
  return best ?? { min: lo, max: hi, ticks: MAX_TICKS }
}

/**
 * Floors to the leading digit of the magnitude: 385 -> 300, 0.0042 -> 0.004.
 * The minimum of an axis that does not start at zero.
 */
export const niceFloor = (value: number): number => {
  const scale = 10 ** Math.floor(Math.log10(value))
  return Math.floor(value / scale) * scale
}

/**
 * Where the axis of bars starts: 0 or below if any value is, else a round
 * minimum under the lowest one (never equal to it, or its bar would be flat).
 * Nothing to draw (not finite) starts at 0.
 */
export const axisMin = (lowest: number): number => {
  if (!Number.isFinite(lowest)) return 0
  if (lowest <= 0) return lowest
  const floor = niceFloor(lowest)
  return floor < lowest ? floor : niceFloor(lowest * 0.99)
}

/** A tick at just the precision it needs: '600M', '4.1M', '1.25M', '0'. */
export const tickLabel = (
  value: number,
  format?: CardFormat | undefined
): string => {
  // Percent cards hold fractions: 0.125 is '12.5%'.
  if (format?.style === 'percent') return `${Number((value * 100).toFixed(1))}%`
  const label = formatAbbrev(value, null, 4)
  return label.includes('.')
    ? label.replace(/0+(?=[A-Z]?$)/, '').replace(/\.(?=[A-Z]?$)/, '')
    : label
}
